import React from "react"
import { makeAutoObservable } from "mobx"
import { t } from "@lingui/macro"

import { FormFields } from "src/lib/form-fields"
import { ISelectedAccessGroup } from "src/types/local-storage"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { IEditAccessDetailItem } from "src/types/access"
import {
    LockAdminService,
    lock_LockResource,
    lock_LockResourceType,
    ConnectorAdminService,
    avy_api_pkg_common_IntegrationWithType,
    lock_LockResourceCreate,
    lock_LockResourceUpdate,
} from "src/api"
import { Channel } from "src/channel"
import { trackModuleEvent } from "src/analytics/helpers/mixpanel_tracking"
import { MixpanelProperties } from "src/analytics/constants/properties"
import { reportError } from "src/lib/report"

export interface IFormFields {
    type: lock_LockResourceType
    name: string
    externalId: string
    segmentIds: number[]
    resourceId: number
    accessGroupId: number
    integrationConnectorId: number
    integrationType: string
    integrationMode: string
}
export class AccessDetailStore {
    static Context = React.createContext<AccessDetailStore | null>(null)
    formFields = new FormFields<IFormFields>({
        segmentIds: [],
        type: "" as lock_LockResourceType,
        name: "",
        externalId: "",
        resourceId: 0,
        accessGroupId: -1,
        integrationConnectorId: 0,
        integrationType: "",
        integrationMode: "parakey",
    })
    itemTypes: string[] = []
    isLoading = false
    accessGroups: ISelectedAccessGroup[] = []
    accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    integrationConnectors: avy_api_pkg_common_IntegrationWithType[] = []

    constructor() {
        makeAutoObservable(this)
    }

    init = async (item?: IEditAccessDetailItem) => {
        if (item != null) {
            const response = await LockAdminService.getV1AdminLockResource1({
                resourceId: item?.row.id,
            })

            // Determine integration mode, integration type, and external ID
            let integrationMode = "parakey"
            let integrationType = ""
            let externalId = ""

            if (
                response.integration_connector_id !== undefined &&
                response.integration_connector_id !== null &&
                response.integration_connector_id > 0
            ) {
                // Using connector mode - determine type by which resource ID is populated
                integrationMode = "connector"
                if (response.accessy_resource_id != null) {
                    integrationType = "accessy"
                    externalId = response.accessy_resource_id
                } else if (response.parakey_resource_id != null) {
                    integrationType = "parakey"
                    externalId = response.parakey_resource_id
                }
            } else {
                // Using parakey direct mode
                integrationMode = "parakey"
                integrationType = "parakey"
                externalId = response.parakey_resource_id ?? ""
            }

            this.formFields.init({
                name: response.name ?? "",
                accessGroupId: response.access_group_id ?? this.accessGroupId,
                externalId: externalId,
                resourceId: response.resource_id ?? 0,
                segmentIds: response.segment_ids ?? [],
                type: response.type ?? ("" as lock_LockResourceType),
                integrationConnectorId: response.integration_connector_id ?? 0,
                integrationType: integrationType,
                integrationMode: integrationMode,
            })
        } else {
            this.formFields.set("accessGroupId", this.accessGroupId)
        }

        // Fetch integration connectors (async, doesn't block form initialization)
        try {
            this.integrationConnectors =
                await ConnectorAdminService.getV1AdminConnectorIntegration({})
        } catch (error) {
            reportError("Failed to fetch integration connectors", error)
        }
    }
    getRequestData = () => {
        const integrationMode = this.formFields.data.integrationMode
        const requestData: lock_LockResourceCreate = {
            access_group_id:
                this.formFields.data.accessGroupId ?? DEFAULT_ACCESS_GROUP.id,
            name: this.formFields.data.name,
            type: this.formFields.data.type ?? "",
        }

        if (integrationMode === "parakey") {
            // Parakey direct mode - no connector
            requestData.parakey_resource_id = this.formFields.data.externalId
        } else if (integrationMode === "connector") {
            // Connector mode - use integration connector and provider-specific resource ID
            requestData.integration_connector_id =
                this.formFields.data.integrationConnectorId
            const integrationType =
                this.formFields.data.integrationType?.toLowerCase()

            if (integrationType === "parakey") {
                requestData.parakey_resource_id =
                    this.formFields.data.externalId
            } else if (integrationType === "accessy") {
                requestData.accessy_resource_id =
                    this.formFields.data.externalId
            }
        }

        return requestData
    }
    getUpdateRequestData = () => {
        const integrationMode = this.formFields.data.integrationMode
        const requestData: lock_LockResourceUpdate = {
            lockResourceID: this.formFields.data.resourceId,
            name: this.formFields.data.name,
        }

        if (integrationMode === "parakey") {
            // Parakey direct mode - no connector
            requestData.parakey_resource_id = this.formFields.data.externalId
        } else if (integrationMode === "connector") {
            // Connector mode - use integration connector and provider-specific resource ID
            requestData.integration_connector_id =
                this.formFields.data.integrationConnectorId
            const integrationType =
                this.formFields.data.integrationType?.toLowerCase()

            if (integrationType === "parakey") {
                requestData.parakey_resource_id =
                    this.formFields.data.externalId
            } else if (integrationType === "accessy") {
                requestData.accessy_resource_id =
                    this.formFields.data.externalId
            }
        }

        return requestData
    }
    createItem = async () => {
        const publishedIn = this.formFields.get("segmentIds")
        let itemResponse: lock_LockResource = {}

        try {
            // Create
            await this.formFields.catchErrors(async () => {
                itemResponse = await LockAdminService.postV1AdminLockResource({
                    request: await this.getRequestData(),
                })
            })
            trackModuleEvent("Access | Create new Access", {
                [MixpanelProperties.ItemID]: itemResponse.resource_id,
            })
            // Publish
            if (
                itemResponse.resource_id != null &&
                publishedIn != null &&
                publishedIn.length > 0
            ) {
                await LockAdminService.putV1AdminLockResourcePublish({
                    resourceId: itemResponse.resource_id as number,
                    request: {
                        published_in: publishedIn,
                    },
                })
            }

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "parakey",
                    action: "update",
                },
            })
        } catch (error) {
            reportError("Failed to create item", error)
        } finally {
            this.setIsLoading(false)
        }
    }

    updateItem = async (item: IEditAccessDetailItem) => {
        const publishedIn = this.formFields.get("segmentIds")
        try {
            // Update
            await this.formFields.catchErrors(async () => {
                await LockAdminService.putV1AdminLockResource({
                    resourceId: item.row.id ?? 0,
                    request: await this.getUpdateRequestData(),
                })
            })

            // Publish
            await LockAdminService.putV1AdminLockResourcePublish({
                resourceId: item.row.id ?? 0,
                request: {
                    published_in: publishedIn,
                },
            })

            Channel.send({
                name: "repository/updated",
                payload: {
                    repository: "parakey",
                    action: "update",
                },
            })
        } catch (error) {
            reportError("Failed to update item", error)
        } finally {
            this.setIsLoading(false)
        }
    }
    setAccessGroups = (accessGroups: ISelectedAccessGroup[]) => {
        this.accessGroups = accessGroups
    }

    setSelectedAccessGroupId = (accessGroupId: number) => {
        this.accessGroupId = accessGroupId
    }

    setItemTypes = (itemTypes: string[]) => {
        this.itemTypes = itemTypes
    }

    validateRequiredFields = (requiredFields: string[]) => {
        this.formFields.clearErrors()

        requiredFields.forEach((field) => {
            const requiredField = field as keyof IFormFields
            const formField = this.formFields.get(requiredField)

            if (
                formField == null ||
                formField === "" ||
                formField === "<p></p>\n"
            ) {
                this.formFields.setError(requiredField, t`errors.required`)
            } else {
                this.formFields.clearError(requiredField)
            }
        })
    }

    setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)
}
